<script lang="ts">
  import { fetchMemberDms, type DmThread, type MemberDmThreads } from '@api';
  import ConversationThread from '@ui/conversation/ConversationThread.svelte';
  import type { ConversationView, DmMessageView, Party } from '@ui/conversation/types';

  /**
   * The mirrored DM conversation with one member, as A CONVERSATION.
   *
   * `<MemberDmThread>` renders the same data as a stacked transcript, which is right on
   * the member page where it sits among other sections and nobody is going to answer
   * from it. Here the reader IS going to answer, so this renders the component every
   * other DM surface renders — the Helper's DM tab and the superadmin explorer both
   * mount `<ConversationThread>` — and inherits what it already knows how to do:
   * open at the newest message, hold position when older pages prepend, keep the
   * composer pinned under the bubbles, and stay at the bottom only while the reader
   * already was.
   *
   * That last list is the reason this is not a bespoke box. Every one of those
   * behaviours was written once, and a drawer with its own scroll-to-bottom and its own
   * composer underneath a transcript is a second, worse copy of all of them.
   *
   * ONE CONVERSATION AT A TIME. A creator has exactly one thread with a member; the
   * superadmin read can return several (`all_owners`), and stacking those would be the
   * scroll this drawer exists to get rid of — so the newest is shown and the rest are
   * offered as a switch.
   */
  let {
    skoolId,
    memberId,
    memberName = 'this member',
    limit = 200,
    pending = [],
    composer,
  }: {
    skoolId: number;
    memberId: number;
    memberName?: string;
    limit?: number;
    /**
     * Messages this browser has just sent, shown before the mirror has them.
     *
     * A DM leaves through the outbox and comes back only once a Helper has sent it AND
     * a crawl has mirrored it — a minute at best. Until then the transcript is a record
     * of a conversation you have visibly just added to and which does not show your
     * message, which reads as "it didn't go".
     *
     * They drop out ON THEIR OWN: any fetched outbound message with the same text
     * supersedes its pending twin, so the real row — with its real id and timestamp —
     * replaces the guess the moment it exists, and nothing has to remember to clean up.
     */
    pending?: DmMessageView[];
    /** The reply box, handed straight to `<ConversationThread>` so it lands inside the
     *  thread rather than under it. */
    composer?: import('svelte').Snippet | undefined;
  } = $props();

  let data = $state<MemberDmThreads | null>(null);
  let failed = $state(false);
  let loading = $state(true);
  /** Which thread is on screen, by channel id. Null follows "the newest". */
  let openId = $state<string | null>(null);

  $effect(() => {
    const sid = skoolId;
    const mid = memberId;
    let alive = true;
    data = null;
    failed = false;
    loading = true;
    openId = null;
    fetchMemberDms(sid, mid, { limit })
      .then((res) => {
        if (alive) data = res;
      })
      .catch(() => {
        if (alive) failed = true;
      })
      .finally(() => {
        if (alive) loading = false;
      });
    return () => {
      alive = false;
    };
  });

  const threads = $derived(data?.items ?? []);
  const open = $derived(threads.find((t) => t.channel_external_id === openId) ?? threads[0] ?? null);

  function ownerName(t: DmThread): string {
    return t.owner_name || t.owner_email || `user ${t.owner_user_id}`;
  }

  /** Whose inbox this is, for the avatar on the owner's own side. Never the viewer —
   *  the superadmin read spans inboxes that are not theirs. */
  function ownerOf(t: DmThread): Party {
    return { name: ownerName(t), pictureUrl: t.owner_picture ?? null };
  }

  const view = $derived<ConversationView | null>(
    open
      ? {
          id: open.channel_external_id,
          name: open.counterpart_name ?? memberName,
          handle: open.counterpart_handle,
          pictureUrl: open.counterpart_picture,
          owner: ownerOf(open),
          preview: open.messages.at(-1)?.content ?? null,
          lastAt: open.last_message_at,
          unread: 0,
        }
      : null,
  );

  /** The stored rows in the shape the bubbles read. A rename, no logic — the two media
   *  lists pass through untouched because `@ui` types them and this route sends what
   *  the other two send (#161). */
  const stored = $derived<DmMessageView[]>(
    (open?.messages ?? []).map((m) => ({
      id: m.external_id,
      content: m.content,
      createdAt: m.created_at,
      outbound: m.outbound,
      videos: m.videos,
      attachments: m.attachments,
    })),
  );

  const messages = $derived<DmMessageView[]>([
    ...stored,
    // Only the ones the mirror has not caught up with. Comparing on the text is enough:
    // these are this browser's own sends from seconds ago, not an attempt to match
    // arbitrary messages.
    ...pending.filter(
      (p) => !stored.some((m) => m.outbound && m.content.trim() === p.content.trim()),
    ),
  ]);

  /**
   * Have they caught up with what we sent?
   *
   * NO COUNTERPART ID IS NEEDED, and that is not a shortcut. `<ConversationThread>`
   * only renders this when the LAST message is outbound, and nobody has their own
   * message unread — so under the one condition where it shows, a non-empty list is
   * necessarily them. (A member thread is 1:1; a group thread would need the id.)
   *
   * `null` is never-recorded, not read: channels older than the column cannot be
   * backfilled, and rendering those as "Read" would invent a fact about a person.
   */
  const receipt = $derived<'sent' | 'read' | null>(
    !open || open.unread_user_external_ids == null
      ? null
      : open.unread_user_external_ids.length > 0
        ? 'sent'
        : 'read',
  );

  /**
   * A conversation that does not exist yet is still a conversation you can start.
   *
   * `<ConversationThread>` needs something to be a thread OF, so an empty one is
   * synthesised from the member. It carries no messages and says so, and the composer
   * under it is the whole point of rendering anything at all here.
   */
  const blank = $derived<ConversationView>({
    id: `member:${memberId}`,
    name: memberName,
    handle: null,
    pictureUrl: null,
    owner: null,
    preview: null,
    lastAt: null,
    unread: 0,
  });
</script>

{#if loading}
  <p class="px-1 text-xs text-base-content/60">Loading conversation…</p>
{:else if failed}
  <p class="px-1 text-xs text-base-content/60">Couldn't load the conversation.</p>
{:else}
  {#if threads.length > 1}
    <!-- Only when there is a choice to make. This used to be the superadmin read
         holding one member's conversation from SEVERAL OWNERS' inboxes, labelled
         "Inbox"; that cannot happen now — every read is one owner's — so what is left
         is the honest case of one owner holding more than one channel with the same
         person, where the pills are just those channels. -->
    <div class="mb-2 flex flex-wrap items-center gap-1.5">
      {#each threads as t (`${t.owner_user_id}:${t.channel_external_id}`)}
        <button
          class="rounded-full px-2.5 py-1 text-[0.7rem] font-medium transition-colors
                 {open?.channel_external_id === t.channel_external_id
                   ? 'bg-primary text-primary-content'
                   : 'bg-base-200/70 text-base-content/70 hover:bg-base-200'}"
          onclick={() => (openId = t.channel_external_id)}
        ><span class="redact">{ownerName(t)}</span></button>
      {/each}
    </div>
  {/if}

  <!-- `compact`: a drawer is a narrow column, the same density the Helper panel uses. -->
  <ConversationThread
    conversation={view ?? blank}
    {messages}
    compact
    {receipt}
    {composer}
  />
{/if}
